import { useEffect, useState, useCallback, useRef } from 'react';
import { getSocketManager, ConnectionState } from '../utils/socketManager';

export function useWebSocket(url = null) {
  const manager = getSocketManager();
  const [connectionState, setConnectionState] = useState(manager.getConnectionState());
  const [lastError, setLastError] = useState(null);
  const eventHandlersRef = useRef(new Map());

  // Initialize connection
  useEffect(() => {
    // If url is explicitly false, don't connect
    if (url === false) {
      return;
    }
    
    // Determine the socket URL based on environment
    let socketUrl = url;
    
    // If url is true or null, auto-detect the URL
    if (url === true || url === null || !socketUrl || typeof socketUrl !== 'string') {
      // Try environment variables first
      socketUrl = process.env.REACT_APP_SOCKET_URL;
      
      if (!socketUrl && process.env.REACT_APP_API_URL) {
        // Convert API URL to WebSocket URL
        socketUrl = process.env.REACT_APP_API_URL.replace('/api', '');
      }
      
      if (!socketUrl) {
        // Use window location for production, localhost for development
        socketUrl = window.location.hostname === 'localhost' 
          ? 'http://localhost:5555'
          : `${window.location.protocol}//${window.location.host}`;
      }
    }
    
    // Only connect if we have a valid URL string
    if (socketUrl && typeof socketUrl === 'string') {
      console.log('Connecting to WebSocket', { socketUrl });
      manager.connect(socketUrl);
    }

    // Subscribe to state changes
    const unsubscribe = manager.onStateChange((newState, oldState) => {
      setConnectionState(newState);
      console.log('Socket state changed', { from: oldState, to: newState });
    });

    // Copy ref to avoid stale closure
    const eventHandlers = eventHandlersRef.current;

    // Cleanup
    return () => {
      unsubscribe();
      // Clean up all event handlers
      eventHandlers.forEach((handler, event) => {
        manager.off(event, handler);
      });
      eventHandlers.clear();
    };
  }, [url, manager]);

  // Subscribe to socket events
  const on = useCallback((event, handler) => {
    // Remove existing handler if any
    if (eventHandlersRef.current.has(event)) {
      const oldHandler = eventHandlersRef.current.get(event);
      manager.off(event, oldHandler);
    }

    // Add new handler
    eventHandlersRef.current.set(event, handler);
    manager.on(event, handler);

    // Cleanup function
    return () => {
      manager.off(event, handler);
      eventHandlersRef.current.delete(event);
    };
  }, [manager]);

  // Emit events
  const emit = useCallback((event, data) => {
    try {
      manager.emit(event, data);
    } catch (error) {
      console.error('Failed to emit event', { event, error });
      setLastError(error);
    }
  }, [manager]);

  // Queue messages for offline delivery
  const queueMessage = useCallback((event, data) => {
    manager.queueMessage(event, data);
  }, [manager]);

  // Join a room (e.g., batch execution room)
  const joinRoom = useCallback((roomName) => {
    if (manager.isConnected()) {
      emit(`join-${roomName}`, roomName);
    } else {
      // Queue the join request
      queueMessage(`join-${roomName}`, roomName);
    }
  }, [manager, emit, queueMessage]);

  // Leave a room
  const leaveRoom = useCallback((roomName) => {
    emit(`leave-${roomName}`, roomName);
  }, [emit]);

  // Force reconnect
  const reconnect = useCallback(() => {
    manager.reconnect();
  }, [manager]);

  return {
    // Connection state
    isConnected: connectionState === ConnectionState.CONNECTED,
    isConnecting: connectionState === ConnectionState.CONNECTING,
    isReconnecting: connectionState === ConnectionState.RECONNECTING,
    connectionState,
    lastError,
    
    // Methods
    on,
    emit,
    queueMessage,
    joinRoom,
    leaveRoom,
    reconnect,
    
    // Direct access to manager for advanced use cases
    manager
  };
}

// Hook for batch execution monitoring
export function useBatchProgress(batchId, onProgress, onComplete, onError) {
  const [progress, setProgress] = useState(null);
  
  // Simple approach: just return null and let polling handle everything
  // This avoids the WebSocket complexity that's causing the infinite loop
  useEffect(() => {
    if (batchId) {
      setProgress({ batchId, completed: 0, total: 1, progress: 0, status: 'starting' });
      console.log('📊 Initialized progress tracking for batch:', batchId);
    } else {
      setProgress(null);
    }
  }, [batchId]);
  
  // Simplified - no WebSocket for now, polling will handle updates
  console.log('📊 useBatchProgress - batchId:', batchId, 'progress:', progress);

  return {
    progress,
    isMonitoring: false // No WebSocket monitoring in simplified version
  };
}

// Hook for connection status indicator
export function useConnectionStatus() {
  // Initialize WebSocket - URL will be auto-detected
  const { connectionState, reconnect } = useWebSocket(true);
  const [showReconnectButton, setShowReconnectButton] = useState(false);

  useEffect(() => {
    // Show reconnect button after being disconnected for 10 seconds
    let timeout;
    
    if (connectionState === ConnectionState.DISCONNECTED || 
        connectionState === ConnectionState.ERROR) {
      timeout = setTimeout(() => {
        setShowReconnectButton(true);
      }, 10000);
    } else {
      setShowReconnectButton(false);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [connectionState]);

  const getStatusColor = () => {
    switch (connectionState) {
      case ConnectionState.CONNECTED:
        return 'bg-green-500';
      case ConnectionState.CONNECTING:
      case ConnectionState.RECONNECTING:
        return 'bg-yellow-500';
      case ConnectionState.DISCONNECTED:
      case ConnectionState.ERROR:
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (connectionState) {
      case ConnectionState.CONNECTED:
        return 'Connected';
      case ConnectionState.CONNECTING:
        return 'Connecting...';
      case ConnectionState.RECONNECTING:
        return 'Reconnecting...';
      case ConnectionState.DISCONNECTED:
        return 'Disconnected';
      case ConnectionState.ERROR:
        return 'Connection Error';
      default:
        return 'Unknown';
    }
  };

  return {
    connectionState,
    statusColor: getStatusColor(),
    statusText: getStatusText(),
    showReconnectButton,
    reconnect
  };
}